import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { Pode } from "@/lib/permissoes";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { ChevronLeft, ChevronRight, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { WhatsAppIcon } from "@/components/icons/whatsapp";
import {
  useTarefasByData, useFuncionarios, useAtividades, useUpdateTarefa, useDeleteTarefa,
  useQuinzenaAtiva, useAdicionaisDaQuinzena,
} from "@/hooks/use-equipe";
import { formatarBRL } from "@shared/equipe-fechamento";
import type { Tarefa } from "@shared/schema";
import { CompartilharPlanoDialog } from "@/components/equipe/compartilhar-plano-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { TarefaDiariaSheet, type TarefaParaEditar, type TarefaStatus } from "@/components/equipe/TarefaDiariaSheet";
import { SemQuinzenaBanner } from "@/components/equipe/SemQuinzenaBanner";
import { SetorChipPicker } from "@/components/equipe/SetorChipPicker";
import { formatSetores } from "@/lib/setores";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { isSemQuinzena, SEM_QUINZENA_MSG } from "@/lib/equipe-errors";
import { format, addDays, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";

import { PageHeader, ALVO_TOQUE_BOTAO } from "@/components/layout/page-header";
import { PageToolbar } from "@/components/layout/page-toolbar";
import { avatarCor } from "@/lib/avatar-cor";
const STATUS_LABEL: Record<string, string> = {
  PENDENTE:     "A fazer",
  EM_ANDAMENTO: "Em andamento",
  CONCLUIDA:    "Concluída",
  CANCELADA:    "Cancelada",
  FALTOU:       "Faltou",
};

const STATUS_CLASS: Record<string, string> = {
  PENDENTE:     "bg-muted text-foreground",
  EM_ANDAMENTO: "bg-atencao-suave text-atencao",
  CONCLUIDA:    "bg-sucesso-suave text-sucesso",
  CANCELADA:    "bg-erro-suave text-erro",
  FALTOU:       "bg-atencao-suave text-atencao",
};

function barClass(status: string): string {
  if (status === "CONCLUIDA")    return "bg-sucesso";
  if (status === "EM_ANDAMENTO") return "bg-info";
  if (status === "FALTOU")       return "bg-atencao";
  if (status === "CANCELADA")    return "bg-erro";
  return "bg-muted";
}


// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_HOJE = { data: "" };

export default function EquipeHoje() {
  // O dia é o recorte da tela: na URL, para reload e "voltar" caírem no mesmo
  // dia. Vazio = hoje, derivado — abrir /equipe/hoje continua sendo "hoje".
  const { estado, definir } = useEstadoNaUrl(PADRAO_HOJE);
  const data = estado.data || format(new Date(), "yyyy-MM-dd");
  const setData = (v: string) => definir({ data: v });
  const [compartilharOpen, setCompartilharOpen] = useState(false);
  const [addTarefaOpen, setAddTarefaOpen] = useState(false);
  const [addDefaultFuncId, setAddDefaultFuncId] = useState<string | undefined>(undefined);
  const [editTarefa, setEditTarefa] = useState<Tarefa | null>(null);
  const [editStatus, setEditStatus] = useState("");
  const [editAtividadeId, setEditAtividadeId] = useState("");
  const [editSetorIds, setEditSetorIds] = useState<string[]>([]);
  const [editObs, setEditObs] = useState("");
  const [concluirTarefa, setConcluirTarefa] = useState<TarefaParaEditar | null>(null);
  const { toast } = useToast();

  const { data: tarefas = [], isLoading: tLoading } = useTarefasByData(data);
  const { data: funcionarios = [], isLoading: fLoading } = useFuncionarios();
  const { data: atividades = [] } = useAtividades();
  const { data: setoresRaw = [] } = useQuery<{ id: string; nome: string; ativo: boolean }[]>({ queryKey: ["/api/setores"] });
  const setores = setoresRaw.filter(s => s.ativo !== false).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const qc = useQueryClient();
  const updateTarefa = useUpdateTarefa();
  const deleteTarefa = useDeleteTarefa();

  // §5.2 — badge "+ R$ X adicional" na tarefa. Vem do bloco 2 do endpoint de
  // adicionais da quinzena (§4.5), agrupado por tarefa aqui no client — não
  // existe endpoint "adicionais por dia", e criar um só para isto duplicaria
  // dado que a quinzena já serve.
  const { data: quinzenaAtiva } = useQuinzenaAtiva();
  const { data: adicionaisQuinzena } = useAdicionaisDaQuinzena(quinzenaAtiva?.id);
  const adicionalTotalPorTarefa = new Map<string, number>();
  for (const a of adicionaisQuinzena?.detalhe ?? []) {
    const centavos = Math.round(parseFloat(a.valor) * 100);
    adicionalTotalPorTarefa.set(a.tarefaId, (adicionalTotalPorTarefa.get(a.tarefaId) ?? 0) + centavos);
  }

  const isLoading = tLoading || fLoading;
  const dataLabel = format(new Date(data + "T00:00:00"), "EEEE, d 'de' MMMM", { locale: ptBR });

  const openEditTarefa = (t: Tarefa) => {
    setEditTarefa(t);
    setEditStatus(t.status);
    setEditAtividadeId(t.atividadeId ?? "");
    const ids = t.setorIds?.length ? t.setorIds : (t.setorId ? [t.setorId] : []);
    setEditSetorIds(ids);
    setEditObs(t.observacoes ?? "");
  };

  const handleSaveEdit = async () => {
    if (!editTarefa) return;
    try {
      await updateTarefa.mutateAsync({
        id: editTarefa.id,
        data: {
          status: editStatus as any,
          atividadeId: editAtividadeId,
          setorIds: editSetorIds,
          setorId: editSetorIds[0] ?? undefined,
          observacoes: editObs.trim() || undefined,
        },
      });
      setEditTarefa(null);
      toast({ title: "Tarefa atualizada!" });
    } catch (err) {
      if (isSemQuinzena(err)) {
        toast({ title: "Crie a quinzena primeiro", description: SEM_QUINZENA_MSG, variant: "destructive" });
      } else {
        toast({ title: "Erro ao salvar", variant: "destructive" });
      }
    }
  };

  const handleDeleteEdit = async () => {
    if (!editTarefa) return;
    try {
      await deleteTarefa.mutateAsync(editTarefa.id);
      setEditTarefa(null);
      toast({ title: "Tarefa removida." });
    } catch {
      toast({ title: "Erro ao remover", variant: "destructive" });
    }
  };

  const openNovaComFunc = (funcId?: string) => {
    setAddDefaultFuncId(funcId);
    setAddTarefaOpen(true);
  };

  const abrirConcluir = (f: (typeof funcionarios)[number], tarefasFunc: Tarefa[]) => {
    const alvo = tarefasFunc.find(t => t.status !== "CONCLUIDA" && t.status !== "FALTOU") ?? tarefasFunc[0];
    if (!alvo) return;
    const ativ = atividades.find(a => a.id === alvo.atividadeId);
    const ids = alvo.setorIds?.length ? alvo.setorIds : (alvo.setorId ? [alvo.setorId] : []);
    setConcluirTarefa({
      id: alvo.id,
      data: alvo.data,
      funcionarioId: alvo.funcionarioId,
      funcionarioNome: f.apelido ?? f.nome,
      atividadeId: alvo.atividadeId ?? "",
      atividadeNome: ativ?.nome ?? "—",
      setorId: alvo.setorId ?? null,
      setorIds: ids,
      setorNome: "",
      tipo: (alvo as any).tipo ?? "NORMAL",
      status: alvo.status as TarefaStatus,
      valor: String(parseFloat(String((alvo as any).valor ?? 0))),
      valorAdicional: (alvo as any).valorAdicional ?? null,
      comentario: (alvo as any).observacoes ?? null,
    });
  };


  const funcsEmCampo = funcionarios.filter(f => tarefas.some(t => t.funcionarioId === f.id));
  const compartilharFuncs = funcsEmCampo;
  const compartilharTarefas = tarefas;
  // A capitalização era classe (`capitalize`) no <p>; o subtítulo do PageHeader é
  // texto simples, então o "sexta-feira" vira "Sexta-feira" aqui mesmo.
  const dataDescricao =
    dataLabel.charAt(0).toUpperCase() + dataLabel.slice(1) +
    (funcsEmCampo.length > 0
      ? ` · ${tarefas.length} tarefa${tarefas.length !== 1 ? "s" : ""} · ${funcsEmCampo.length} colaborador${funcsEmCampo.length !== 1 ? "es" : ""}`
      : "");

  return (
    <div className="p-6 space-y-6">
      <SemQuinzenaBanner />
      <PageHeader
        titulo="Hoje no campo"
        descricao={dataDescricao}
        acoesSecundarias={
          <Button variant="outline" className={ALVO_TOQUE_BOTAO} onClick={() => setCompartilharOpen(true)}>
            <WhatsAppIcon className="h-4 w-4 mr-2 text-[#25D366]" />Compartilhar
          </Button>
        }
        acao={
          <Button className={ALVO_TOQUE_BOTAO} onClick={() => openNovaComFunc()}>
            <Plus className="h-4 w-4 mr-2" />Nova tarefa
          </Button>
        }
      />

      <PageToolbar>
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => setData(format(subDays(new Date(data + "T00:00:00"), 1), "yyyy-MM-dd"))}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button variant="outline" className="shrink-0" onClick={() => setData(format(new Date(), "yyyy-MM-dd"))}>Hoje</Button>
        <Button variant="outline" size="icon" className="shrink-0" onClick={() => setData(format(addDays(new Date(data + "T00:00:00"), 1), "yyyy-MM-dd"))}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </PageToolbar>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
      ) : funcsEmCampo.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <p>Nenhuma tarefa planejada para esta data.</p>
          <Button className="mt-4" onClick={() => openNovaComFunc()}>
            <Plus className="h-4 w-4 mr-2" />Adicionar tarefa
          </Button>
        </div>
      ) : (
        <div className="bg-card border rounded-xl divide-y overflow-hidden">
          {funcsEmCampo.map(f => {
            const tarefasFunc = tarefas.filter(t => t.funcionarioId === f.id);
            const nome = f.apelido ?? f.nome;
            return (
              <div key={f.id} className="flex items-start gap-3 px-3.5 py-2.5">
                {/* Avatar */}
                <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-white font-semibold text-sm mt-0.5" style={{ backgroundColor: avatarCor(f.id) }}>
                  {f.nome[0].toUpperCase()}
                </div>
                {/* Corpo */}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight">
                    {nome}
                    <span className="text-xs text-muted-foreground font-normal"> · {f.cargo}</span>
                  </p>
                  {tarefasFunc.map(t => {
                    const ativ = atividades.find(a => a.id === t.atividadeId);
                    const ids = t.setorIds?.length ? t.setorIds : (t.setorId ? [t.setorId] : []);
                    const setorLabel = formatSetores(ids, setores);
                    return (
                      <button
                        key={t.id}
                        onClick={() => openEditTarefa(t)}
                        title={t.observacoes ?? undefined}
                        className="w-full flex items-center gap-2 mt-1 text-left"
                      >
                        <div className={`w-0.5 self-stretch min-h-[16px] rounded-full shrink-0 ${barClass(t.status)}`} />
                        <span className="flex-1 min-w-0 text-[13px] truncate">
                          <span className={t.status === "CONCLUIDA" ? "line-through text-muted-foreground" : "font-medium"}>
                            {t.status === "FALTOU" ? "Faltou" : (ativ?.nome ?? "Atividade")}
                          </span>
                          {setorLabel && <span className="text-muted-foreground"> · {setorLabel}</span>}
                        </span>
                        {/* Tarefa de mensalista: fluxo idêntico, mas fica explícito na
                            lista do dia que ela não vira dinheiro na quinzena. */}
                        {((t as any).tipo ?? "NORMAL") === "MENSALISTA" && t.status !== "FALTOU" && (
                          <Badge variant="outline" className="text-xs shrink-0 bg-info-suave text-info border-info/30">
                            Registro (sem valor)
                          </Badge>
                        )}
                        {/* §5.2 — o adicional é natureza SEPARADA da diária: nunca soma
                            visualmente dentro do valor dela, sempre um "+" ao lado. */}
                        {adicionalTotalPorTarefa.has(t.id) && (
                          <Badge variant="outline" className="text-xs shrink-0 bg-sucesso-suave text-sucesso border-sucesso/30">
                            + {formatarBRL((adicionalTotalPorTarefa.get(t.id) ?? 0) / 100)} adicional
                          </Badge>
                        )}
                        <Badge variant="outline" className={`text-xs shrink-0 ${STATUS_CLASS[t.status]}`}>
                          {/* Falta de mensalista NÃO desconta (salário é fixo): o rótulo
                              diz isso na cara, senão o gestor fica na dúvida se marcar a
                              falta tirou dinheiro do salário dele. */}
                          {t.status === "FALTOU" && ((t as any).tipo ?? "NORMAL") === "MENSALISTA"
                            ? "Falta — sem desconto"
                            : STATUS_LABEL[t.status]}
                        </Badge>
                      </button>
                    );
                  })}
                </div>
                {/* Botão Concluir */}
                {(() => {
                  const temPendente = tarefasFunc.some(t => t.status !== "CONCLUIDA" && t.status !== "FALTOU");
                  if (!temPendente) return null;
                  return (
                    <button
                      onClick={() => abrirConcluir(f, tarefasFunc)}
                      className="flex items-center gap-1 h-7 px-2 rounded-md text-xs font-medium text-sucesso border border-sucesso/30 hover:bg-sucesso-suave transition-colors self-center shrink-0"
                      aria-label={`Concluir tarefa de ${nome}`}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      <span className="hidden sm:inline">Concluir</span>
                    </button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      )}

      {/* Dialog Editar Tarefa */}
      <Dialog open={!!editTarefa} onOpenChange={open => { if (!open) setEditTarefa(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Tarefa</DialogTitle>
          </DialogHeader>
          {editTarefa && (() => {
            const func = funcionarios.find(f => f.id === editTarefa.funcionarioId);
            return (
              <div className="space-y-4">
                {/* Contexto (read-only) */}
                <div className="flex items-center gap-3 p-3 rounded-md bg-muted/40 border">
                  <div className="h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-white font-semibold text-sm" style={{ backgroundColor: avatarCor(func?.id) }}>
                    {func?.nome[0].toUpperCase() ?? "?"}
                  </div>
                  <div>
                    <p className="text-sm font-medium">{func?.apelido ?? func?.nome}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(editTarefa.data + "T00:00:00"), "EEEE, d 'de' MMMM", { locale: ptBR })}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <div className="space-y-1">
                  <Label>Status</Label>
                  <select
                    value={editStatus}
                    onChange={e => setEditStatus(e.target.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-card appearance-none"
                  >
                    {Object.entries(STATUS_LABEL).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                {/* Atividade */}
                <div className="space-y-1">
                  <Label>Atividade</Label>
                  <select
                    value={editAtividadeId}
                    onChange={e => setEditAtividadeId(e.target.value)}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring bg-card appearance-none"
                  >
                    <option value="">Selecione...</option>
                    {atividades.map(a => (
                      <option key={a.id} value={a.id}>{a.nome}</option>
                    ))}
                  </select>
                </div>

                {/* Setores — multi */}
                <div className="space-y-1">
                  <Label>
                    Setor(es){" "}
                    <span className="text-muted-foreground font-normal">
                      {editSetorIds.length > 0 ? `— ${formatSetores(editSetorIds, setores)}` : "(opcional)"}
                    </span>
                  </Label>
                  <SetorChipPicker
                    selectedIds={editSetorIds}
                    onChange={setEditSetorIds}
                    setores={setores}
                  />
                </div>

                {/* Observações */}
                <div className="space-y-1">
                  <Label>Observações <span className="text-muted-foreground font-normal">(opcional)</span></Label>
                  <Textarea
                    value={editObs}
                    onChange={e => setEditObs(e.target.value)}
                    placeholder="Instruções para o colaborador..."
                    rows={2}
                  />
                </div>
              </div>
            );
          })()}
          <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
            {/* Apagar tarefa é `equipe.diarias.editar` (até ENCARREGADO) — o CAMPO
                não vê o botão, e a API recusaria de qualquer jeito. */}
            <Pode cap="equipe.diarias.editar" senao={<span />}>
              <Button
                variant="ghost"
                size="sm"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDeleteEdit}
                disabled={deleteTarefa.isPending}
              >
                <Trash2 className="h-4 w-4 mr-1" />Remover
              </Button>
            </Pode>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditTarefa(null)}>Cancelar</Button>
              <Button onClick={handleSaveEdit} disabled={!editAtividadeId || updateTarefa.isPending}>
                {updateTarefa.isPending ? "Salvando..." : "Salvar"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialogs */}
      <CompartilharPlanoDialog
        open={compartilharOpen}
        onClose={() => setCompartilharOpen(false)}
        data={data}
        funcionarios={compartilharFuncs}
        tarefas={compartilharTarefas}
        atividades={atividades}
        setores={setores}
      />

      {/* Sheet unificado de criação de tarefa */}
      <TarefaDiariaSheet
        open={addTarefaOpen}
        mode="create"
        dataInicial={data}
        defaultFuncionarioId={addDefaultFuncId}
        funcionarios={funcionarios.map(f => ({ id: f.id, nome: f.nome, apelido: f.apelido, valorDiaria: (f as any).valorDiaria ?? null, tipoRemuneracao: (f as any).tipoRemuneracao ?? null }))}
        atividades={atividades}
        setores={setores}
        onClose={() => setAddTarefaOpen(false)}
        onSuccess={() => {
          setAddTarefaOpen(false);
          qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
        }}
      />

      {/* Sheet de conclusão rápida */}
      <TarefaDiariaSheet
        open={!!concluirTarefa}
        mode="edit"
        tarefa={concluirTarefa}
        initialStatus="CONCLUIDA"
        funcionarios={funcionarios.map(f => ({ id: f.id, nome: f.nome, apelido: f.apelido, valorDiaria: (f as any).valorDiaria ?? null, tipoRemuneracao: (f as any).tipoRemuneracao ?? null }))}
        atividades={atividades}
        setores={setores}
        onClose={() => setConcluirTarefa(null)}
        onSuccess={() => {
          setConcluirTarefa(null);
          qc.invalidateQueries({ queryKey: ["/api/equipe/tarefas"] });
        }}
      />
    </div>
  );
}
